import type { GetVintedCatalogTreeQuery } from '@/shared/api/types/generated';
import type { CascaderOption } from '@/shared/types/options';

/** Nœud de l'arbre rendu par l'API, quel que soit son niveau de profondeur. */
type CatalogNode = { id: number; title: string; children?: readonly CatalogNode[] | null };

export type CatalogTree = GetVintedCatalogTreeQuery['getVintedCatalogTree'];

/**
 * Arbre API → options du Cascader AntD.
 *
 * ⚠️ `CascaderOption.value` est une CHAÎNE alors que `id` est un `Int` : la conversion est explicite.
 * L'oublier ne casse rien à la compilation (le Cascader accepte la valeur) mais fait échouer la
 * comparaison de sélection au clic, donc rien ne se sélectionne — panne silencieuse.
 *
 * ⚠️ Aucun tri ici : l'API rend déjà les nœuds triés par nom localisé (cf le docblock du chantier).
 */
export const toCascaderOptions = (nodes: readonly CatalogNode[]): CascaderOption[] =>
  nodes.map((node) => ({
    value: String(node.id),
    label: node.title,
    ...(node.children?.length ? { children: toCascaderOptions(node.children) } : {}),
  }));

/**
 * Index id → chemin complet racine→nœud, construit en une passe.
 *
 * ⚠️ Le Cascader (`value={categoriesPaths}`) et `MobileCategoryDrilldown` (`value: string[][]`)
 * exigent des chemins COMPLETS, pas l'id seul : rendre `[id]` afficherait un Cascader sans sélection
 * après l'import d'une URL. C'est exactement ce que faisait l'ancien parcours du JSON.
 */
export const buildCatalogIdPathMap = (nodes: readonly CatalogNode[]): Map<number, number[]> => {
  const map = new Map<number, number[]>();
  const walk = (node: CatalogNode, parentPath: readonly number[]) => {
    const path = [...parentPath, node.id];
    map.set(node.id, path);
    for (const child of node.children ?? []) walk(child, path);
  };
  for (const root of nodes) walk(root, []);
  return map;
};
